import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, logoutUser,postGoogleAuth } from "../../api";
import { LocalStorage, requestHandler } from "../../utils";
import { toast } from "react-toastify";

// Create a context to manage authentication-related data and functions
const AuthContext = createContext({
	user: null,
	token: null,
	login: async () => {},
	loginWithGoogle: async () => {},
	register: async () => {},
	logout: async () => {},
	syncAuthFromStorage: () => {},
	updateAvatar: () => {},
});

const useAuth = () => {
	return useContext(AuthContext);
};

// Create a component that provides authentication-related data and functions
const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [accessToken, setAccessToken] = useState(
		LocalStorage.get("accessToken") || null,
	);

	const navigate = useNavigate();

	const syncAuthFromStorage = () => {
		try {
			const _token = LocalStorage.get("accessToken");
			const rawUser = LocalStorage.get("user");
			const _user =
				typeof rawUser === "string" ? JSON.parse(rawUser) : rawUser || null;

			if (_token && _user?._id) {
				setUser(_user);
				setAccessToken(_token);
			}
		} catch (error) {
			console.error("Error parsing user data from local storage:", error);
		}
	};

	// Function to handle user login
	const login = async (email, password, rememberMe) => {
		await requestHandler(
			async () => await loginUser({ email, password, rememberMe }),
			null,
			({ result }) => {
				// Update user and access token in the context and local storage
				setUser(result.user);
				setAccessToken(result.accessToken);

				LocalStorage.set("accessToken", result.accessToken);
				LocalStorage.set("user", JSON.stringify(result.user)); // Save user and token in local storage

				toast.success("Login Successfully!");
				navigate("/propertyhome", { replace: true });
			},
			(message) => toast.error(message), // Display error alerts on request failure
		);
	};

	const loginWithGoogle = async (code, setLoading) => {
		await requestHandler(
			async () => await postGoogleAuth({ code ,intent:"login"}),
			setLoading ?? null,
			(data) => {
				const { result } = data;
				if (!result?.accessToken) {
					toast.error("Invalid response from Google sign-in");
					return;
				}
				setUser(result.user);
				setAccessToken(result.accessToken);
				LocalStorage.set("accessToken", result.accessToken);
				LocalStorage.set("user", JSON.stringify(result.user));
				toast.success("Login Successfully!");
				navigate("/propertyhome", { replace: true });
			},
			(message) => toast.error(message),
		);
	};

	// Function to handle user logout
	// const logout = async () => {
	//   await requestHandler(
	//     async () => await logoutUser(),
	//     null,
	//     () => {
	//       setUser(null);
	//       setAccessToken(null);
	//       LocalStorage.clear(); // Clear local storage on logout
	//       console.log("cleared");
	//     },
	//     (message) => toast.error(message), // Display error alerts on request failure
	//   );
	// };
	const logout = async () => {
		try {
			await logoutUser(); // try server logout
		} catch (error) {
			console.warn("Logout API failed, forcing local logout", error);
		} finally {
			// ALWAYS run this
			setUser(null);
			setAccessToken(null);
			LocalStorage.clear();
			navigate("/login", { replace: true });
		}
	};
	// Check for saved user and token in local storage during component initialization
	useEffect(() => {
		syncAuthFromStorage();
	}, []);

	// Provide authentication-related data and functions through the context
	return (
		<AuthContext.Provider
			value={{
				user,
				login,
				loginWithGoogle,
				logout,
				accessToken,
				syncAuthFromStorage,
			}}
		>
			{children} {/* Display a loader while loading */}
		</AuthContext.Provider>
	);
};

// Export the context, provider component
export { useAuth, AuthProvider };
